/**
 * Helper Functions
 * 
 * Utility functions that don't fit into formatters or constants.
 * Includes color mapping, clipboard operations, file downloads, etc.
 * 
 * All functions are defensive and handle edge cases gracefully.
 */

import {
  CALL_STATUS,
  USER_SENTIMENT,
  CALL_DIRECTION,
  DISCONNECTION_REASON,
} from './constants';

// ============================================================================
// COLOR HELPERS
// ============================================================================

/**
 * Get Tailwind color class for call status
 * Returns appropriate color for badges and indicators
 * 
 * @param {string} status - Call status from CALL_STATUS
 * @returns {object} Object with bg, text, and border color classes
 * 
 * @example
 * getStatusColor(CALL_STATUS.ENDED) 
 * // { bg: 'bg-neutral-100', text: 'text-neutral-700', border: 'border-neutral-300' }
 */
export const getStatusColor = (status) => {
  // Default/fallback colors
  const defaultColors = {
    bg: 'bg-neutral-100',
    text: 'text-neutral-700',
    border: 'border-neutral-300',
  };

  if (!status) return defaultColors;

  const colorMap = {
    [CALL_STATUS.REGISTERED]: {
      bg: 'bg-primary-50',
      text: 'text-primary-700',
      border: 'border-primary-200',
    },
    [CALL_STATUS.NOT_CONNECTED]: {
      bg: 'bg-error-50',
      text: 'text-error-700',
      border: 'border-error-200',
    },
    [CALL_STATUS.ONGOING]: {
      bg: 'bg-success-50',
      text: 'text-success-700',
      border: 'border-success-200',
    },
    [CALL_STATUS.ENDED]: {
      bg: 'bg-neutral-100',
      text: 'text-neutral-700',
      border: 'border-neutral-300',
    },
    [CALL_STATUS.ERROR]: {
      bg: 'bg-error-50',
      text: 'text-error-700',
      border: 'border-error-200',
    },
  };

  return colorMap[status] || defaultColors;
};

/**
 * Get Tailwind color class for user sentiment
 * 
 * @param {string} sentiment - User sentiment from USER_SENTIMENT
 * @returns {object} Object with bg, text, and border color classes
 * 
 * @example
 * getSentimentColor(USER_SENTIMENT.POSITIVE)
 * // { bg: 'bg-success-50', text: 'text-success-700', border: 'border-success-200' }
 */
export const getSentimentColor = (sentiment) => {
  const defaultColors = {
    bg: 'bg-neutral-100',
    text: 'text-neutral-700',
    border: 'border-neutral-300',
  };

  if (!sentiment) return defaultColors;

  const colorMap = {
    [USER_SENTIMENT.POSITIVE]: {
      bg: 'bg-success-50',
      text: 'text-success-700',
      border: 'border-success-200',
    },
    [USER_SENTIMENT.NEGATIVE]: {
      bg: 'bg-error-50',
      text: 'text-error-700',
      border: 'border-error-200',
    },
    [USER_SENTIMENT.NEUTRAL]: {
      bg: 'bg-primary-50',
      text: 'text-primary-700',
      border: 'border-primary-200',
    },
    [USER_SENTIMENT.UNKNOWN]: {
      bg: 'bg-neutral-100',
      text: 'text-neutral-500',
      border: 'border-neutral-200',
    },
  };

  return colorMap[sentiment] || defaultColors;
};

/**
 * Get Tailwind color class for call direction
 * 
 * @param {string} direction - Call direction from CALL_DIRECTION
 * @returns {object} Object with bg, text, and border color classes
 * 
 * @example
 * getDirectionColor(CALL_DIRECTION.INBOUND)
 * // { bg: 'bg-primary-50', text: 'text-primary-700', border: 'border-primary-200' }
 */
export const getDirectionColor = (direction) => {
  const defaultColors = {
    bg: 'bg-neutral-100',
    text: 'text-neutral-700',
    border: 'border-neutral-300',
  };

  if (!direction) return defaultColors;

  const colorMap = {
    [CALL_DIRECTION.INBOUND]: {
      bg: 'bg-primary-50',
      text: 'text-primary-700',
      border: 'border-primary-200',
    },
    [CALL_DIRECTION.OUTBOUND]: {
      bg: 'bg-warning-50',
      text: 'text-warning-700',
      border: 'border-warning-200',
    },
  };

  return colorMap[direction] || defaultColors;
};

/**
 * Get color for disconnection reason
 * Categorizes reasons into success (green), warning (orange), or error (red)
 * 
 * @param {string} reason - Disconnection reason from DISCONNECTION_REASON
 * @returns {object} Object with bg, text, and border color classes
 */
export const getDisconnectionColor = (reason) => {
  const defaultColors = {
    bg: 'bg-neutral-100',
    text: 'text-neutral-700',
    border: 'border-neutral-300',
  };

  if (!reason) return defaultColors;

  // Normal/successful endings (green)
  const successReasons = [
    DISCONNECTION_REASON.USER_HANGUP,
    DISCONNECTION_REASON.AGENT_HANGUP,
    DISCONNECTION_REASON.CALL_TRANSFER,
  ];

  // Warning/neutral reasons (orange)
  const warningReasons = [
    DISCONNECTION_REASON.VOICEMAIL_REACHED,
    DISCONNECTION_REASON.INACTIVITY,
    DISCONNECTION_REASON.MAX_DURATION_REACHED,
    DISCONNECTION_REASON.DIAL_BUSY,
    DISCONNECTION_REASON.DIAL_NO_ANSWER,
  ];

  // Everything else is an error (red)
  
  if (successReasons.includes(reason)) {
    return {
      bg: 'bg-success-50',
      text: 'text-success-700',
      border: 'border-success-200',
    };
  }

  if (warningReasons.includes(reason)) {
    return {
      bg: 'bg-warning-50',
      text: 'text-warning-700',
      border: 'border-warning-200',
    };
  }

  // Error reasons
  return {
    bg: 'bg-error-50',
    text: 'text-error-700',
    border: 'border-error-200',
  };
};

// ============================================================================
// CLIPBOARD HELPERS
// ============================================================================

/**
 * Copy text to clipboard
 * Uses modern Clipboard API with fallback for older browsers
 * 
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} True if successful, false otherwise
 * 
 * @example
 * await copyToClipboard("call_123abc")
 * // Returns true if copied successfully
 */
export const copyToClipboard = async (text) => {
  if (!text) {
    console.warn('copyToClipboard: No text provided');
    return false;
  }

  try {
    // Modern Clipboard API (preferred)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    return successful;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// ============================================================================
// FILE DOWNLOAD HELPERS
// ============================================================================

/**
 * Download a file from a URL
 * Creates a temporary anchor element and triggers download
 * 
 * @param {string} url - URL of the file to download
 * @param {string} filename - Desired filename for the download
 * @returns {Promise<boolean>} True if successful, false otherwise
 * 
 * @example
 * await downloadFile("https://example.com/recording.wav", "call_recording.wav")
 */
export const downloadFile = async (url, filename) => {
  if (!url) {
    console.warn('downloadFile: No URL provided');
    return false;
  }

  try {
    // Fetch the file
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Convert to blob
    const blob = await response.blob();

    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    link.style.display = 'none';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);

    return true;
  } catch (error) {
    console.error('Failed to download file:', error);
    return false;
  }
};

/**
 * Download text content as a file
 * Useful for exporting transcripts, logs, etc.
 * 
 * @param {string} content - Text content to download
 * @param {string} filename - Desired filename
 * @param {string} mimeType - MIME type (default: 'text/plain')
 * @returns {boolean} True if successful, false otherwise
 * 
 * @example
 * downloadTextFile("Call transcript...", "transcript.txt")
 * downloadTextFile(JSON.stringify(data), "data.json", "application/json")
 */
export const downloadTextFile = (content, filename, mimeType = 'text/plain') => {
  if (!content) {
    console.warn('downloadTextFile: No content provided');
    return false;
  }

  try {
    // Create blob from content
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    // Create temporary anchor
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download.txt';
    link.style.display = 'none';

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error('Failed to download text file:', error);
    return false;
  }
};

// ============================================================================
// URL HELPERS
// ============================================================================

/**
 * Build query string from object
 * Handles arrays and nested objects
 * Skips null/undefined values
 * 
 * @param {object} params - Object with query parameters
 * @returns {string} Query string (without leading ?)
 * 
 * @example
 * buildQueryString({ status: 'ended', limit: 50, tags: ['a', 'b'] })
 * // "status=ended&limit=50&tags=a&tags=b"
 */
export const buildQueryString = (params) => {
  if (!params || typeof params !== 'object') return '';

  const pairs = [];

  Object.keys(params).forEach(key => {
    const value = params[key];

    // Skip null/undefined
    if (value === null || value === undefined) return;

    // Handle arrays
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== null && item !== undefined) {
          pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        }
      });
    } else {
      // Handle single values
      pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });

  return pairs.join('&');
};

/**
 * Parse query string into object
 * 
 * @param {string} queryString - Query string (with or without leading ?)
 * @returns {object} Parsed parameters
 * 
 * @example
 * parseQueryString("?status=ended&limit=50")
 * // { status: 'ended', limit: '50' }
 */
export const parseQueryString = (queryString) => {
  if (!queryString) return {};

  // Remove leading ? if present
  const cleanQuery = queryString.startsWith('?') 
    ? queryString.slice(1) 
    : queryString;

  const params = {};

  cleanQuery.split('&').forEach(pair => {
    const [key, value] = pair.split('=');
    if (key) {
      const decodedKey = decodeURIComponent(key);
      const decodedValue = value ? decodeURIComponent(value) : '';

      // Handle duplicate keys (convert to array)
      if (params[decodedKey]) {
        if (Array.isArray(params[decodedKey])) {
          params[decodedKey].push(decodedValue);
        } else {
          params[decodedKey] = [params[decodedKey], decodedValue];
        }
      } else {
        params[decodedKey] = decodedValue;
      }
    }
  });

  return params;
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if a value is empty
 * Handles strings, arrays, objects, null, undefined
 * 
 * @param {*} value - Value to check
 * @returns {boolean} True if empty, false otherwise
 * 
 * @example
 * isEmpty('') // true
 * isEmpty([]) // true
 * isEmpty({}) // true
 * isEmpty(null) // true
 * isEmpty('hello') // false
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Check if a string is a valid email
 * Basic email validation (not RFC-compliant, but good enough for UI)
 * 
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 * 
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('invalid') // false
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Check if a string is a valid phone number
 * Accepts various formats: +1234567890, (123) 456-7890, 123-456-7890, etc.
 * 
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone format
 * 
 * @example
 * isValidPhone('+12137771234') // true
 * isValidPhone('(213) 777-1234') // true
 * isValidPhone('invalid') // false
 */
export const isValidPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Should have at least 10 digits (US) or up to 15 (international)
  return digits.length >= 10 && digits.length <= 15;
};

// ============================================================================
// DEBOUNCE HELPER
// ============================================================================

/**
 * Create a debounced function
 * Delays execution until after wait milliseconds have elapsed since last call
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} Debounced function
 * 
 * @example
 * const debouncedSearch = debounce((query) => {
 *   console.log('Searching for:', query);
 * }, 500);
 * 
 * debouncedSearch('hello'); // Won't execute immediately
 * debouncedSearch('hello world'); // Cancels previous, waits 500ms
 */
export const debounce = (func, wait = 300) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// ============================================================================
// ARRAY HELPERS
// ============================================================================

/**
 * Remove duplicates from an array
 * 
 * @param {Array} array - Array with potential duplicates
 * @returns {Array} Array with unique values
 * 
 * @example
 * uniqueArray([1, 2, 2, 3, 3, 3]) // [1, 2, 3]
 */
export const uniqueArray = (array) => {
  if (!Array.isArray(array)) return [];
  return [...new Set(array)];
};

/**
 * Sort array of objects by a property
 * 
 * @param {Array} array - Array to sort
 * @param {string} key - Property to sort by
 * @param {string} order - 'asc' or 'desc' (default: 'asc')
 * @returns {Array} Sorted array
 * 
 * @example
 * sortByKey([{name: 'Bob'}, {name: 'Alice'}], 'name')
 * // [{name: 'Alice'}, {name: 'Bob'}]
 */
export const sortByKey = (array, key, order = 'asc') => {
  if (!Array.isArray(array)) return [];

  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal === bVal) return 0;

    const comparison = aVal > bVal ? 1 : -1;
    return order === 'asc' ? comparison : -comparison;
  });
};

